import dayjs from 'dayjs'
import type { TFunction } from 'i18next'
import {
  Star, ThumbsUp, ThumbsDown, Check, X, Download, Circle, CircleDot, Square, CheckSquare,
} from 'lucide-react'
import { Subtype } from '@/api/model/subtype'
import { ChecklistItemAnswer } from '@/api/model/checklistItemAnswer'
import { EvaluationThumbsAnswer } from '@/api/model/evaluationThumbsAnswer'
import { SurveyEventAnswer } from '@/api/model/surveyEventAnswer'
import { ActionModuleTypeIcon } from '@/pages/groups/components/ActionModuleIcon'

// The subtype definition (from module.subtypes) and the instance response data are
// polymorphic in the backend; the renderer is selected solely via
// `definition.subtype` (enum `Subtype`). Within a renderer the concrete type is
// guaranteed, so we read the fields directly (partly via cast, because Swagger doesn't
// type the dropdown variant).
// `Summary`/`Details` are called as plain functions (not as a JSX component),
// so hooks are not allowed here — `t` therefore comes as a prop from the two
// callers (SubtypeSummaryCell/InstanceSubtypeDetailsDialog), which use `useTranslation` themselves.
export interface SubtypeRendererProps {
  definition: any
  data: any
  t: TFunction
}

interface SubtypeRenderer {
  Summary: (props: SubtypeRendererProps) => React.ReactNode
  Details: (props: SubtypeRendererProps) => React.ReactNode
}

const IMG_STYLE = 'max-h-[400px] max-w-full object-contain'

// ─── Checklist / Dropdown (strukturgleich) ─────────────────────────────────────

function checklistSummary({ definition, data, t }: SubtypeRendererProps) {
  const answers: Record<string, string> = data.answers ?? {}
  const items: { id: number; text: string | null }[] = definition.checklistItems ?? []
  const type: string = definition.checklistAnswerType

  if (type === 'SingleChoice') {
    const positiveId = Object.keys(answers).find((id) => answers[id] === ChecklistItemAnswer.CheckedPositive)
    const item = items.find((i) => String(i.id) === positiveId)
    return <span className="truncate">{item?.text ?? '—'}</span>
  }
  const checked = Object.values(answers).filter((a) => a === ChecklistItemAnswer.CheckedPositive).length
  return <span className="truncate">{t('subtype.checkedItems', { checked, total: items.length })}</span>
}

function checklistDetails({ definition, data }: SubtypeRendererProps) {
  const answers: Record<string, string> = data.answers ?? {}
  const items: { id: number; text: string | null }[] = definition.checklistItems ?? []
  const type: string = definition.checklistAnswerType

  return (
    <div className="flex flex-col gap-1.5">
      {items.map((item) => {
        const answer = answers[String(item.id)]
        const positive = answer === ChecklistItemAnswer.CheckedPositive

        if (type === 'MultipleChoiceWithDecline') {
          if (!answer || answer === ChecklistItemAnswer.NotChecked) return null
          return (
            <div key={item.id} className="flex items-center gap-2 text-sm">
              {positive ? <Check className="size-4 text-green-600" /> : <X className="size-4 text-destructive" />}
              <span>{item.text}</span>
            </div>
          )
        }

        const Icon = type === 'SingleChoice'
          ? (positive ? CircleDot : Circle)
          : (positive ? CheckSquare : Square)
        return (
          <div key={item.id} className="flex items-center gap-2 text-sm">
            <Icon className={`size-4 ${positive ? 'text-primary' : 'text-muted-foreground'}`} />
            <span>{item.text}</span>
          </div>
        )
      })}
    </div>
  )
}

const checklistRenderer: SubtypeRenderer = { Summary: checklistSummary, Details: checklistDetails }

// ─── Date ───────────────────────────────────────────────────────────────────

function fmtSelectedDate(selectedDate: string | null | undefined) {
  if (!selectedDate) return '—'
  const d = new Date(selectedDate)
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('de-DE')
}

const dateRenderer: SubtypeRenderer = {
  Summary: ({ data }) => <span className="truncate">{fmtSelectedDate(data.selectedDate)}</span>,
  Details: ({ data }) => <span className="text-sm">{fmtSelectedDate(data.selectedDate)}</span>,
}

// ─── EvaluationStars (Summary == Details) ──────────────────────────────────────

function stars({ data }: SubtypeRendererProps) {
  const n: number = data.numberOfStars ?? 0
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={`size-4 ${n >= i ? 'fill-primary text-primary' : 'text-muted-foreground'}`}
        />
      ))}
    </div>
  )
}

const starsRenderer: SubtypeRenderer = { Summary: stars, Details: stars }

// ─── EvaluationThumbs (Summary == Details) ─────────────────────────────────────

function thumbs({ data }: SubtypeRendererProps) {
  if (data.answer === EvaluationThumbsAnswer.ThumbsUp) return <ThumbsUp className="size-4 text-green-600" />
  if (data.answer === EvaluationThumbsAnswer.ThumbsDown) return <ThumbsDown className="size-4 text-destructive" />
  return null
}

const thumbsRenderer: SubtypeRenderer = { Summary: thumbs, Details: thumbs }

// ─── PictureUpload (genau ein Bild) ─────────────────────────────────────────────

const pictureUploadRenderer: SubtypeRenderer = {
  // Old portal shows a fixed "1 image" in the summary, without reading data fields.
  Summary: ({ t }) => <span className="truncate">{t('subtype.numberUploadedImages', { count: 1 })}</span>,
  Details: ({ data, t }) => (
    <div className="flex flex-col gap-2">
      <p className="text-sm font-medium">{t('subtype.uploadedImage')}</p>
      {data.uploadedImageUrl && <img src={data.uploadedImageUrl} alt="" className={IMG_STYLE} />}
    </div>
  ),
}

// ─── MultiPictureUpload ─────────────────────────────────────────────────────────

const multiPictureUploadRenderer: SubtypeRenderer = {
  Summary: ({ data, t }) => {
    const images: any[] = data.images ?? []
    return (
      <div className="flex items-center gap-2">
        <span className="truncate">{t('subtype.numberUploadedImages', { count: images.length })}</span>
        {data.bundleDownloadUrl && (
          <a href={data.bundleDownloadUrl} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-foreground">
            <Download className="size-4" />
          </a>
        )}
      </div>
    )
  },
  Details: ({ data, t }) => {
    const images: any[] = data.images ?? []
    return (
      <div className="flex flex-col gap-3">
        <p className="text-sm font-medium">{t('subtype.uploadedImages')}</p>
        <div className="flex flex-wrap gap-4">
          {images.map((img) => (
            <img key={img.id} src={img.uploadedImageUrl} alt="" className={IMG_STYLE} />
          ))}
        </div>
        {data.bundleDownloadUrl && (
          <a href={data.bundleDownloadUrl} target="_blank" rel="noreferrer" className="inline-flex w-fit items-center gap-2 rounded-md border px-3 py-1.5 text-sm hover:bg-muted">
            <Download className="size-4" />
            {t('subtype.downloadBundle')}
          </a>
        )}
      </div>
    )
  },
}

// ─── TextAnswer ───────────────────────────────────────────────────────────────

const textAnswerRenderer: SubtypeRenderer = {
  Summary: ({ data }) => <span className="truncate">{data.textAnswer ?? '—'}</span>,
  Details: ({ data }) => (
    <p className="whitespace-pre-wrap rounded-md border bg-muted/30 p-2 text-sm">{data.textAnswer ?? '—'}</p>
  ),
}

// ─── SurveyAppointment ──────────────────────────────────────────────────────────

const surveyAppointmentRenderer: SubtypeRenderer = {
  Summary: ({ data, t }) => {
    if (data.noEventViable) return <span className="truncate">{t('subtype.noEventSelected')}</span>
    const answers: any[] = data.eventAnswers ?? []
    return <span className="truncate">{answers.map((e) => e.event?.title).filter(Boolean).join(', ')}</span>
  },
  Details: ({ data, t }) => {
    if (data.noEventViable) return <span className="text-sm">{t('subtype.noEventSelected')}</span>
    const answers: any[] = data.eventAnswers ?? []
    return (
      <div className="flex flex-col gap-2">
        {answers.map((e, i) => {
          const ev = e.event ?? {}
          const sameDay = ev.startDate === ev.endDate
          return (
            <div key={i} className="flex items-center gap-3">
              <ActionModuleTypeIcon module={ev} className="size-6" />
              <div className="flex flex-col">
                <span className="text-sm">{ev.title}</span>
                <span className="text-xs text-muted-foreground">
                  {ev.startDate && dayjs(ev.startDate).format('DD.MM.YYYY HH:mm [Uhr]')}
                </span>
                {!sameDay && ev.endDate && (
                  <span className="text-xs text-muted-foreground">
                    {dayjs(ev.endDate).format('DD.MM.YYYY HH:mm [Uhr]')}
                  </span>
                )}
              </div>
              {e.answer === SurveyEventAnswer.Accepted
                ? <Check className="ml-auto size-4 text-green-600" />
                : e.answer === SurveyEventAnswer.Declined
                  ? <X className="ml-auto size-4 text-destructive" />
                  : null}
            </div>
          )
        })}
      </div>
    )
  },
}

// ─── Registry ───────────────────────────────────────────────────────────────

// Only these subtypes are rendered; all others (Bonus/Signature/PresenceQuota/
// Welcome/…) deliberately render empty in both Summary AND Detail — like the old portal.
export const SUBTYPE_RENDERERS: Partial<Record<string, SubtypeRenderer>> = {
  [Subtype.ChecklistAnswerModuleSubtype]: checklistRenderer,
  [Subtype.DropdownAnswerModuleSubtype]: checklistRenderer,
  [Subtype.DateModuleSubtype]: dateRenderer,
  [Subtype.EvaluationStarsModuleSubtype]: starsRenderer,
  [Subtype.EvaluationThumbsModuleSubtype]: thumbsRenderer,
  [Subtype.PictureUploadModuleSubtype]: pictureUploadRenderer,
  [Subtype.MultiPictureUploadModuleSubtype]: multiPictureUploadRenderer,
  [Subtype.TextAnswerModuleSubtype]: textAnswerRenderer,
  [Subtype.SurveyAppointmentModuleSubtype]: surveyAppointmentRenderer,
}
